import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Images } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { buildRoute } from '@/config/routes';

type Props = {
  propertyId: string;
  hasPhotos: boolean;
  has3DTour: boolean;
};

type IconButtonProps = {
  label: string;
  icon: typeof Images;
  onClick: () => void;
};

const IconButton = ({ label, icon: Icon, onClick }: IconButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          onClick();
        }}
        className="text-foreground hover:text-primary bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 rounded-sm"
      >
        <Icon size={21} />
      </button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

/**
 * Property-list cell surfacing whether a property has photos and/or a 3D tour
 * as clickable Lucide icons that deep-link into the Manage Assets page. The
 * underlying hasPhotos / has3DTour flags come from the row (see propertyConfig
 * PropertyTable); the cell renders an em-dash when neither is present.
 */
export function RecentAssetsCell({ propertyId, hasPhotos, has3DTour }: Props) {
  const navigate = useNavigate();

  if (!hasPhotos && !has3DTour) {
    // Match the empty-value placeholder the other list cells use (ListViewCell).
    return <Typography variant="body-medium">-</Typography>;
  }

  return (
    // One shared provider so hovering from one icon straight to the sibling
    // skips the open delay (skipDelayDuration), and the timing is configured
    // in a single place.
    <TooltipProvider delayDuration={300} skipDelayDuration={300}>
      <div className="flex items-center gap-7">
        {hasPhotos && (
          <IconButton
            label="View photos"
            icon={Images}
            onClick={() => navigate(buildRoute.propertyAssets(propertyId))}
          />
        )}
        {has3DTour && (
          <IconButton
            label="View 3D tour"
            icon={Box}
            onClick={() =>
              navigate(buildRoute.propertyAssets(propertyId, { tab: 'zillow' }))
            }
          />
        )}
      </div>
    </TooltipProvider>
  );
}
